import { Box, Table, Tag, Text, VStack } from "@chakra-ui/react";

import { ARRIVAL_RATES_FREQUENCIES, LEVELS } from "../../../store/constants";
import AgentTicketStatusGraphs from "../charts/AgentTicketStatusGraphs";
import { formatNumber } from "../../../store/helpers";
import ReportDataTable from "../../reports/ReportDataTable";
import {
  AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
} from "../../../store/renderConfig";
import { getTicketsPerAgentRowsByBranch } from "../../../store/ticketsPerAgentExport";

const PercentMeta = ({ value }) => (
  <Text color="gray.500" fontWeight="semibold" fontSize="xs" textTransform="uppercase">
    ({Number(value || 0).toFixed(2)}%)
  </Text>
);

export const renderTicketsPerAgentCell = (agentData, columnId) => {
  if (columnId === "agentName") return agentData.agentName;
  if (columnId === "total") {
    return <Text color="var(--green)">{formatNumber(agentData.total)}</Text>;
  }
  if (columnId === "percentage") return `${Number(agentData.totalPct || 0).toFixed(2)}%`;
  if (columnId === "average") return formatNumber(agentData.average);
  if (columnId === "min") return formatNumber(agentData.min);
  if (columnId === "max") return formatNumber(agentData.max);
  if (columnId === "completed") {
    return (
      <VStack gap="2px" align="start">
        <Text color="var(--green)">{formatNumber(agentData.numCompleted)}</Text>
        <PercentMeta value={agentData.pctCompleted} />
      </VStack>
    );
  }
  if (columnId === "noShow") {
    return (
      <VStack gap="2px" align="start">
        <Text color="var(--red)">{formatNumber(agentData.numNoShow)}</Text>
        <PercentMeta value={agentData.pctNoShow} />
      </VStack>
    );
  }
  if (columnId === "unattended") {
    return (
      <VStack gap="2px" align="start">
        <Text color="var(--red)">{formatNumber(agentData.numUnattended)}</Text>
        <PercentMeta value={agentData.pctUnattended} />
      </VStack>
    );
  }
  return null;
};

const ChartedAgentTable = ({ agentData, index, totalBranchTickets, branchName, branchIndex }) => {
  const {
    min,
    max,
    total,
    average,
    numCompleted,
    numNoShow,
    numUnattended,
    pctCompleted,
    pctNoShow,
    pctUnattended,
    agentName,
    charts,
  } = agentData;
  const totalPct = (total / totalBranchTickets) * 100 || 0;
  const title = `${branchIndex + 1}.${index + 1}. ${agentName}`;

  return total ? (
    <Box className="reportContainer ticketsPerAgentBlock" mt="20px">
      <h3>{title}</h3>
      <Tag.Root>
        <Tag.Label className="branchTag">{branchName} Branch</Tag.Label>
      </Tag.Root>
      <Table.Root className="stripedTable" size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>#</Table.ColumnHeader>
            <Table.ColumnHeader borderRight="1px solid #dadce0">Total Tickets</Table.ColumnHeader>
            <Table.ColumnHeader borderRight="1px solid #dadce0">Average</Table.ColumnHeader>
            <Table.ColumnHeader>Min</Table.ColumnHeader>
            <Table.ColumnHeader>Max</Table.ColumnHeader>
            <Table.ColumnHeader>Completed</Table.ColumnHeader>
            <Table.ColumnHeader>No Show</Table.ColumnHeader>
            <Table.ColumnHeader>Unattended</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.Cell>{index + 1}</Table.Cell>
            <Table.Cell borderRight="1px solid #dadce0">
              <VStack gap="2px" align="start">
                <Text color="var(--green)">{formatNumber(total)}</Text>
                <PercentMeta value={totalPct} />
              </VStack>
            </Table.Cell>
            <Table.Cell borderRight="1px solid #dadce0">{formatNumber(average)}</Table.Cell>
            <Table.Cell>{formatNumber(min)}</Table.Cell>
            <Table.Cell>{formatNumber(max)}</Table.Cell>
            <Table.Cell>
              <VStack gap="2px" align="start">
                <Text color="var(--green)">{formatNumber(numCompleted)}</Text>
                <PercentMeta value={pctCompleted} />
              </VStack>
            </Table.Cell>
            <Table.Cell>
              <VStack gap="2px" align="start">
                <Text color="var(--red)">{formatNumber(numNoShow)}</Text>
                <PercentMeta value={pctNoShow} />
              </VStack>
            </Table.Cell>
            <Table.Cell>
              <VStack gap="2px" align="start">
                <Text color="var(--red)">{formatNumber(numUnattended)}</Text>
                <PercentMeta value={pctUnattended} />
              </VStack>
            </Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell colSpan={8}>
              <Box mt="20px" mb="20px">
                <AgentTicketStatusGraphs
                  charts={charts}
                  frequency={ARRIVAL_RATES_FREQUENCIES.DAY}
                  level={LEVELS.BRANCH}
                  title={`${agentName} Tickets, Day by Day Comparison`}
                />
              </Box>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table.Root>
    </Box>
  ) : null;
};

const TicketsPerAgentTable = ({ data, branchIndex, renderConfig, onRenderConfigChange }) => {
  const { agentsData = [], branchName } = data;
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  const totalBranchTickets = agentsData.reduce((sum, agent) => sum + (Number(agent.total) || 0), 0);

  return (
    <Box>
      <Text>{agentsData.length} agents.</Text>
      {!agentsData.length ? <Text fontSize="sm">No data available for this report</Text> : null}

      {agentsData.length && !config.charts.enabled ? (
        <Box className="ticketsPerAgentBlock" mt="16px" mb="24px">
          <ReportDataTable
            columns={AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS}
            rows={getTicketsPerAgentRowsByBranch(data, config)}
            renderConfig={config}
            renderCell={renderTicketsPerAgentCell}
            presortedRows={true}
            onSortChange={(sort) => onRenderConfigChange?.({ ...config, sort })}
          />
        </Box>
      ) : null}

      {agentsData.length && config.charts.enabled
        ? agentsData.map((agentData, index) => (
            <ChartedAgentTable
              key={index}
              agentData={agentData}
              index={index}
              totalBranchTickets={totalBranchTickets}
              branchName={branchName}
              branchIndex={branchIndex}
            />
          ))
        : null}
    </Box>
  );
};

export default TicketsPerAgentTable;
